import React, { useState } from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { Delete, Edit, Add, Visibility, ExpandMore, ExpandLess } from "@mui/icons-material";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import useUpdate from "../../../../api/useUpdate";
import useDelete from "../../../../api/useDelete";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";

const MedicineCategory = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [parentCategory, setParentCategory] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openSubcategoriesDialog, setOpenSubcategoriesDialog] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);

  const { data, refetch, isLoading } = useFetchObjects(
    ["medicine-categories"],
    "medicine-categories",
    token
  );

  const { handleAdd, loading: addLoading } = useAdd(
    "medicine-categories",
    token
  );
  const { handleUpdate, loading: updateLoading } = useUpdate(
    "medicine-categories",
    token
  );
  const { handleDelete, ConfirmDialog } = useDelete(
    "medicine-categories",
    token
  );

  const handleOpenAdd = () => {
    setEditMode(false);
    setCategoryName("");
    setParentCategory(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (category) => {
    setEditMode(true);
    setSelectedId(category.id);
    setCategoryName(category.name);
    const parent = category.parent ? data?.find(c => c.id === category.parent) : null;
    setParentCategory(parent || null);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setCategoryName("");
    setParentCategory(null);
    setSelectedId(null);
  };

  const handleSubmit = async () => {
    const payload = {
      name: categoryName,
      parent: parentCategory?.id || null,
    };

    if (editMode) {
      await handleUpdate(selectedId, payload);
    } else {
      await handleAdd(payload);
    }
    handleClose();
    refetch();
  };

  const filteredData = data?.filter((category) => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (category.parent_name && category.parent_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const isParentCategory = !category.parent;
    return matchesSearch && isParentCategory;
  });

  const getSubcategories = (parentId) => {
    return data?.filter(cat => cat.parent === parentId) || [];
  };

  const handleViewSubcategories = (categoryId) => {
    setSelectedParentId(categoryId);
    setOpenSubcategoriesDialog(true);
  };

  const toggleRowExpansion = (categoryId) => {
    setExpandedRows(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ boxShadow: "none", m: "1em 3em" }}>
        <Grid container padding="20px">
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              {t("pharmacy.medicineCategories")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAdd}
            >
              {t("pharmacy.addCategory")}
            </Button>
          </Grid>

          <TextField
            fullWidth
            placeholder={t("pharmacy.search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mt: 3 }}
          />

          {isLoading ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <TableContainer sx={{ mt: 4 }}>
              <Table>
                <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                  <TableRow>
                    <TableCell align="center">{t("pharmacy.id")}</TableCell>
                    <TableCell align="center">{t("pharmacy.name")}</TableCell>
                    <TableCell align="center">
                      {t("pharmacy.parentCategory")}
                    </TableCell>
                    <TableCell align="center">
                      {t("pharmacy.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData?.map((category) => {
                    const subcategories = getSubcategories(category.id);
                    const isExpanded = expandedRows.includes(category.id);
                    return (
                    <React.Fragment key={category.id}>
                    <TableRow>
                      <TableCell align="center">
                        {subcategories.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(category.id)}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                        {category.id}
                      </TableCell>
                      <TableCell align="center">{category.name}</TableCell>
                      <TableCell align="center">
                        {category.parent_name || "-"}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenEdit(category)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={() => handleDelete(category.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {isExpanded && subcategories.map((sub) => (
                      <TableRow key={`sub-${sub.id}`} sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell align="center" sx={{ pl: 6 }}>↳ {sub.id}</TableCell>
                        <TableCell align="center">{sub.name}</TableCell>
                        <TableCell align="center">{category.name}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenEdit(sub)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="secondary"
                            onClick={() => handleDelete(sub.id)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    </React.Fragment>
                  );})}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editMode
                ? t("pharmacy.editCategory")
                : t("pharmacy.addNewCategory")}
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label={t("pharmacy.categoryName")}
                fullWidth
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
              />
              <Autocomplete
                options={data?.filter(cat => cat.id !== selectedId) || []}
                getOptionLabel={(option) => option.name}
                value={parentCategory}
                onChange={(event, newValue) => setParentCategory(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label={t("pharmacy.parentCategory")}
                    placeholder={t("pharmacy.searchParentCategory")}
                  />
                )}
                fullWidth
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>{t("pharmacy.cancel")}</Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={!categoryName || addLoading || updateLoading}
                startIcon={
                  (addLoading || updateLoading) && <CircularProgress size={20} />
                }
              >
                {editMode ? t("pharmacy.update") : t("pharmacy.add")}
              </Button>
            </DialogActions>
          </Dialog>

          <ConfirmDialog />
        </Grid>
      </Paper>
    </Grid>
  );
};

export default MedicineCategory;
